<!--
  second-version-esign.component.html  (PARENT)
  ─────────────────────────────────────────────────────────────────────────────
  CHILD COMPONENTS:
    <app-electronic-sign #electronicSign>   → standard e-sign (draw/style/upload)
    <app-aadhaar-sign    #aadhaarSign>       → Aadhaar iframe / DSC form-POST

  ROUTING LOGIC (set by parent TS after getSignerDetails()):
    showEmudhra = false  →  render <app-electronic-sign>
    showEmudhra = true   →  render <app-aadhaar-sign>

  The nav-bar "Set Signature" / "Finish & Submit" buttons forward to the
  child via template reference calls:
    triggerElectronicSign()  →  electronicSignRef.getParallelSignData()
    triggerSubmitSign()      →  electronicSignRef.SubmitSign()
  ─────────────────────────────────────────────────────────────────────────────
-->

<div class="page-wrapper">
  <!-- ═══════════════════════════════════════════════════════════
       LOGO BAR
  ════════════════════════════════════════════════════════════ -->
  <div class="logo-bar" *ngIf="!isDocumentSigned">
    <img
      src="../../assets/img/new-logo.png"
      height="36"
      class="logo"
      alt="Logo"
    />
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       NAV BAR
  ════════════════════════════════════════════════════════════ -->
  <div class="nav-bar" *ngIf="!isDocumentSigned">
    <div class="nav-left">
      <div class="tab-pill-container">
        <button
          class="nav-tab"
          [class.nav-tab--active]="activeTab === 'esignature'"
          (click)="setActiveTab('esignature')"
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
            <path
              d="M20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
            />
          </svg>
          eSignature
        </button>
        <div class="tab-sep"></div>
        <button
          class="nav-tab"
          [class.nav-tab--active]="activeTab === 'translate'"
          (click)="setActiveTab('translate')"
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path
              d="M3 5h12M9 3v2m-3.232 9.232a7.5 7.5 0 0010.607 0M21 21l-6-6m6 6l-6-6"
            />
            <path d="M11.5 7.5l2 5m-8 0l2-5 2 5m-5 0h5" />
          </svg>
          Translate Agreement
        </button>
      </div>

      <div class="nav-divider"></div>

      <!-- Features dropdown -->
      <div class="features-dropdown">
        <button
          class="features-btn"
          (click)="showFeaturesDropdown = !showFeaturesDropdown"
        >
          <span class="features-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
              <path
                d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z"
              />
            </svg>
          </span>
          <span class="features-label">{{ selectedFeatureLabel }}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            [style.transform]="
              showFeaturesDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
            "
            style="transition: transform 0.2s ease"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <div class="features-menu" *ngIf="showFeaturesDropdown">
          <button
            class="features-menu-item"
            *ngFor="let opt of featureOptions"
            [class.features-menu-item--active]="selectedFeature === opt.value"
            (click)="
              selectedFeature = opt.value;
              showFeaturesDropdown = false;
              onFeatureChange(opt.value)
            "
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Nav-bar right: Sign Now / Finish buttons only for electronic sign -->
    <div class="nav-right">
      <ng-container *ngIf="activeTab === 'esignature' && !showEmudhra">
        <button
          *ngIf="!signImage"
          class="sign-now-nav-btn"
          (click)="triggerElectronicSign()"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
            <path
              d="M20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
            />
          </svg>
          Set Signature
        </button>
        <button
          *ngIf="signImage"
          class="finish-nav-btn"
          (click)="triggerSubmitSign()"
          [disabled]="!isChecked || !electronicSignRef?.isSignatureValid"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
          >
            <path
              d="M20 6L9 17l-5-5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Finish &amp; Submit
        </button>
      </ng-container>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       SIGNED PAGE  (shown after document is signed / cancelled / expired)
  ════════════════════════════════════════════════════════════ -->
  <div class="signed-page" *ngIf="isDocumentSigned">
    <div class="signed-card">
      <img
        *ngIf="signatoryDetails.status === 'COMPLETED'"
        src="/assets/img/animation-crop.gif"
        class="sign-anim"
        alt=""
      />
      <div class="signed-logo">
        <img src="../../../assets/img/new-logo.png" height="52" alt="Logo" />
      </div>

      <ng-container *ngIf="signatoryDetails.status === 'COMPLETED'">
        <div class="signed-icon-wrap">
          <img src="../../../assets/img/sign-completed.png" height="60" />
        </div>
        <h3 class="signed-title">Document Signed Successfully</h3>
        <p class="signed-subtitle">
          Thank you for signing. Your document is on its way.
        </p>
        <div class="signed-actions">
          <button
            class="sa-btn sa-btn--email"
            (click)="shareToEmail()"
            [disabled]="isEmailButtonDisabled"
            [class.sa-btn--disabled]="isEmailButtonDisabled"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
              />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <span *ngIf="!isEmailButtonDisabled">Email to me</span>
            <span *ngIf="isEmailButtonDisabled"
              >Wait {{ emailCooldownTime }}s</span
            >
          </button>
          <button class="sa-btn sa-btn--print" (click)="printDocument()">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6 9 6 2 18 2 18 9" />
              <path
                d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"
              />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </button>
          <button class="sa-btn sa-btn--download" (click)="downloadDocument()">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>
        </div>
      </ng-container>

      <ng-container *ngIf="signatoryDetails.status === 'CANCELLED'">
        <div class="signed-icon-wrap">
          <img src="../../../assets/img/sign-cancelled.png" height="160" />
        </div>
        <h3 class="signed-title">The Signature is Cancelled.</h3>
      </ng-container>

      <ng-container *ngIf="signatoryDetails.status === 'EXPIRED'">
        <div class="signed-icon-wrap">
          <img src="../../../assets/img/sign-cancelled.png" height="160" />
        </div>
        <h3 class="signed-title">The Signature is Expired.</h3>
      </ng-container>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       MAIN CONTENT
  ════════════════════════════════════════════════════════════ -->
  <div
    class="main-content"
    *ngIf="!isDocumentSigned"
    [class.main-content--consent-active]="displayConsentModal"
  >
    <!-- ── eSIGNATURE TAB ── -->
    <ng-container *ngIf="activeTab === 'esignature'">
      <!-- LEFT: PDF viewer (desktop) -->
      <div class="doc-side" *ngIf="showSign">
        <div class="doc-toolbar">
          <span class="doc-title" [title]="signatoryDetails?.document_name">
            {{ signatoryDetails?.document_name || "Loading document..." }}
          </span>
          <div class="doc-toolbar-controls">
            <button
              class="ctrl-btn"
              (click)="pageNo > 1 ? pageChange(pageNo - 1) : null"
              title="Previous page"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
            <input
              class="page-input"
              type="number"
              [min]="1"
              [max]="numPdfPages"
              [(ngModel)]="pageJumpValue"
              (keydown.enter)="jumpToPage()"
              (blur)="jumpToPage()"
            />
            <span class="page-sep-text">/ {{ numPdfPages || "—" }}</span>
            <button
              class="ctrl-btn"
              (click)="pageNo < numPdfPages ? pageChange(pageNo + 1) : null"
              title="Next page"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div class="ctrl-sep"></div>
            <button class="ctrl-btn" (click)="zoomOut()" title="Zoom out">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </button>
            <span class="zoom-level">{{ zoomLevel }}%</span>
            <button class="ctrl-btn" (click)="zoomIn()" title="Zoom in">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </button>
            <button
              class="ctrl-btn"
              *ngIf="zoomLevel !== 100"
              (click)="resetZoom()"
              title="Reset zoom"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
            <div class="ctrl-sep"></div>
            <button class="ctrl-btn" title="Secure document">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </button>
          </div>
        </div>
        <div class="pdf-card">
          <div style="position: relative">
            <div class="example-boundary">
              <ng-container
                *ngFor="let item of positionDetails?.[pageVariable] || []"
              >
                <div
                  class="custom_button_1"
                  [ngClass]="{
                    'example-box': !signImage,
                    'example-box_sign': signImage
                  }"
                  cdkDragBoundary=".example-boundary"
                  [style.transform]="getPosition(item)"
                  [style.width.px]="getBoxSizePx(item).width"
                  [style.height.px]="getBoxSizePx(item).height"
                ></div>
              </ng-container>
            </div>
            <div [style.zoom]="zoomLevel / 100">
              <pdf-viewer
                [src]="pdfUrl"
                [(page)]="pageVariable"
                [original-size]="true"
                [show-all]="true"
                [render-text]="false"
                [fit-to-page]="true"
                (after-load-complete)="pdfLoaded($event)"
                style="width: 100%; overflow-y: scroll; display: block"
              >
              </pdf-viewer>
            </div>
          </div>
        </div>
      </div>

      <!-- LEFT: PDF viewer (mobile) -->
      <div class="doc-side-mobile" *ngIf="!showSign && pdfUrl">
        <div class="doc-toolbar">
          <span class="doc-title">{{
            signatoryDetails?.document_name || "Loading document..."
          }}</span>
        </div>
        <div class="pdf-card">
          <div style="zoom: 0.55; height: 38vh; overflow: hidden">
            <pdf-viewer
              [src]="pdfUrl"
              [(page)]="pageVariable"
              [original-size]="true"
              [show-all]="true"
              [render-text]="false"
              [fit-to-page]="true"
              (after-load-complete)="pdfLoaded($event)"
              style="width: 100%"
            >
            </pdf-viewer>
          </div>
        </div>
      </div>

      <!-- RIGHT: Summarize overlay OR eSignature panel -->
      <ng-container *ngIf="isSummarizeActive; else esignatureRightPanel">
        <div class="right-panel right-panel--summarize">
          <ng-container *ngTemplateOutlet="summarizePanel"></ng-container>
        </div>
      </ng-container>

      <ng-template #esignatureRightPanel>
        <div class="right-panel">
          <!-- Signer image card -->
          <div class="panel-card">
            <div class="card-label">Signer Image</div>
            <div class="signer-body">
              <div class="signer-img-box">
                <img
                  *ngIf="webcamImage"
                  [src]="webcamImage"
                  alt="Signer"
                  class="signer-photo"
                />
                <div *ngIf="!webcamImage" class="signer-placeholder">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c9d0da"
                    stroke-width="1.3"
                  >
                    <path
                      d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
                    />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <span>No photo captured</span>
                </div>
              </div>
              <button class="retake-btn" (click)="reTakeImage(userImage)">
                {{ webcamImage ? "Re-Take" : "Capture" }}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
                  />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Signature preview (only for electronic sign) -->
          <div class="panel-card" *ngIf="signImage && !showEmudhra">
            <div class="card-label">Your Signature</div>
            <div class="signer-body">
              <div class="signer-img-box signer-img-box--sig">
                <img [src]="signImage" alt="Signature" class="sig-preview" />
              </div>
              <button
                class="retake-btn"
                data-bs-toggle="modal"
                data-bs-target="#signModel"
              >
                Re-Sign
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Signature details -->
          <div class="panel-card">
            <div class="card-label">Signature Details</div>
            <div class="sig-details-row">
              <div class="sig-detail">
                <span class="sd-key">Name:</span
                ><span class="sd-val">{{ signatoryDetails?.name || "—" }}</span>
              </div>
              <div class="sig-detail">
                <span class="sd-key">Order ID:</span
                ><span class="sd-val">{{ orderId || "—" }}</span>
              </div>
              <div class="sig-detail">
                <span class="sd-key">Email:</span
                ><span class="sd-val">{{
                  signatoryDetails?.email || "—"
                }}</span>
              </div>
              <div class="sig-detail">
                <span class="sd-key">Contact:</span
                ><span class="sd-val">{{
                  signatoryDetails?.contact_number || "—"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Summary -->
          <div
            class="panel-card"
            *ngIf="
              zeroSigners.length > 0 ||
              completedSigners.length > 0 ||
              afterSigners.length > 0 ||
              signatoryDetails?.signatory_sequence === 1 ||
              signatoryDetails?.status === 'EXPIRED'
            "
          >
            <div class="card-label">Summary</div>
            <div class="summary-body">
              <p class="s-item">
                &#9658; The document will be sent to your email upon completion
                by all signers.
              </p>
              <p
                class="s-item"
                *ngIf="signatoryDetails?.signatory_sequence === 1"
              >
                &#9658; You are the first one to open the Document.
              </p>
              <ng-container *ngFor="let signer of zeroSigners; let last = last">
                <p class="s-item">
                  &#9658; E-sign link has been sent to
                  <strong>{{ signer.name }}</strong
                  >{{ !last ? "," : "." }}
                </p>
              </ng-container>
              <ng-container *ngFor="let signer of completedSigners">
                <p class="s-item">
                  &#9658; Document has been Signed by
                  <strong>{{ signer.name }}</strong
                  >.
                </p>
              </ng-container>
              <ng-container *ngIf="afterSigners.length > 0">
                <p class="s-item s-item--muted">
                  After your signing is completed:
                </p>
                <ng-container *ngFor="let signer of afterSigners">
                  <p class="s-item">
                    &#9658; E-sign link will be sent to
                    <strong>{{ signer.name }}</strong
                    >.
                  </p>
                </ng-container>
              </ng-container>
              <p
                class="s-item s-item--warn"
                *ngIf="signatoryDetails?.status === 'EXPIRED'"
              >
                The Signature is Expired.
              </p>
            </div>
          </div>

          <!-- ─────────────────────────────────────────────────────────────
               CHILD COMPONENTS — rendered here inside the right panel
               showEmudhra controls which child is active
          ──────────────────────────────────────────────────────────────── -->

          <!-- ELECTRONIC SIGN child -->
          <ng-container *ngIf="!showEmudhra">
            <app-electronic-sign
              #electronicSign
              [signatoryDetails]="signatoryDetails"
              [orderId]="orderId"
              [esignId]="esignId"
              [signatoryId]="signatoryId"
              [positionDetails]="positionDetails"
              [userData]="userData"
              [webcamImage]="webcamImage"
              [enableCaptureImage]="enableCaptureImage"
              [isChecked]="isChecked"
              [orderType]="orderType"
              [base64Data]="base64Data"
              [signPosition]="signPosition"
              (signCompleted)="onElectronicSignCompleted()"
              (signImageChange)="onSignImageChange($event)"
            >
            </app-electronic-sign>
          </ng-container>

          <!-- AADHAAR / DSC SIGN child -->
          <ng-container *ngIf="showEmudhra">
            <app-aadhaar-sign
              #aadhaarSign
              [signatoryDetails]="signatoryDetails"
              [orderId]="orderId"
              [esignId]="esignId"
              [signatoryId]="signatoryId"
              [webcamImage]="webcamImage"
              [enableCaptureImage]="enableCaptureImage"
              [userData]="userData"
              [isChecked]="isChecked"
              (signCompleted)="onAadhaarSignCompleted()"
            >
            </app-aadhaar-sign>
          </ng-container>

          <!-- Capture image button (shared, shown when no webcamImage yet) -->
          <div class="panel-card" *ngIf="enableCaptureImage && !webcamImage">
            <div class="action-body">
              <button class="capture-btn" (click)="loadCam(userImage)">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
                  />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                Capture Image First
              </button>
            </div>
          </div>
        </div>
      </ng-template> </ng-container
    ><!-- /esignature tab -->

    <!-- ── TRANSLATE TAB ── -->
    <ng-container *ngIf="activeTab === 'translate'">
      <div class="translate-split-left">
        <div class="doc-toolbar">
          <div class="translate-doc-info">
            <div
              class="translate-doc-icon"
              *ngIf="signatoryDetails?.document_name"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                />
                <path d="M14 2v6h6" />
              </svg>
            </div>
            <span class="doc-title" [title]="signatoryDetails?.document_name">
              {{ signatoryDetails?.document_name || "Loading document..." }}
            </span>
          </div>
          <div class="doc-toolbar-controls">
            <button
              class="ctrl-btn"
              (click)="pageNo > 1 ? pageChange(pageNo - 1) : null"
              title="Previous page"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
            <input
              class="page-input"
              type="number"
              [min]="1"
              [max]="numPdfPages"
              [(ngModel)]="pageJumpValue"
              (keydown.enter)="jumpToPage()"
              (blur)="jumpToPage()"
            />
            <span class="page-sep-text">/ {{ numPdfPages || "—" }}</span>
            <button
              class="ctrl-btn"
              (click)="pageNo < numPdfPages ? pageChange(pageNo + 1) : null"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div class="ctrl-sep"></div>
            <button class="ctrl-btn" (click)="zoomOut()">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </button>
            <span class="zoom-level">{{ zoomLevel }}%</span>
            <button class="ctrl-btn" (click)="zoomIn()">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </button>
            <button
              class="ctrl-btn"
              *ngIf="zoomLevel !== 100"
              (click)="resetZoom()"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
            <div class="ctrl-sep"></div>
            <button class="ctrl-btn" title="Secure document">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </button>
          </div>
        </div>
        <div class="pdf-card">
          <div [style.zoom]="zoomLevel / 100">
            <pdf-viewer
              [src]="pdfUrl"
              [(page)]="pageVariable"
              [original-size]="true"
              [show-all]="true"
              [render-text]="false"
              [fit-to-page]="true"
              style="width: 100%; overflow-y: scroll; display: block"
            >
            </pdf-viewer>
          </div>
        </div>
      </div>

      <div class="translate-split-divider"></div>

      <div class="translate-split-right">
        <ng-container *ngIf="isSummarizeActive">
          <ng-container *ngTemplateOutlet="summarizePanel"></ng-container>
        </ng-container>

        <ng-container
          *ngIf="!isSummarizeActive && !isTranslating && !translatedHtml"
        >
          <div class="translate-selector-panel">
            <h2 class="translate-hero-title">Translate Documents in Seconds</h2>
            <p class="translate-hero-sub">
              Upload your Word or PDF and get fast, reliable translations side
              by side.
            </p>
            <div
              class="translate-lang-card"
              [class.translate-lang-card--error]="showLanguageError"
            >
              <div class="translate-lang-inner">
                <span class="translate-lang-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    stroke-width="1.8"
                  >
                    <path
                      d="M3 5h12M9 3v2m-3.232 9.232a7.5 7.5 0 0010.607 0M21 21l-6-6m6 6l-6-6"
                    />
                    <path d="M11.5 7.5l2 5m-8 0l2-5 2 5m-5 0h5" />
                  </svg>
                </span>
                <app-dropdown
                  [options]="supportedLanguages"
                  optionLabel="label"
                  optionValue="value"
                  class="translate-lang-select"
                  [(ngModel)]="selectedLanguage"
                  (onChange)="showLanguageError = false; onLanguageChange()"
                  [disabled]="isLoadingLanguages"
                  [placeholder]="
                    isLoadingLanguages
                      ? 'Loading languages...'
                      : 'Select Language'
                  "
                ></app-dropdown>
                <span class="lang-spinner" *ngIf="isLoadingLanguages"></span>
                <svg
                  *ngIf="!isLoadingLanguages"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  stroke-width="2.5"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
              <p class="translate-lang-error-msg" *ngIf="showLanguageError">
                Please select a language before translating.
              </p>
            </div>
            <button class="translate-cta-btn" (click)="translateDocument()">
              Translate
            </button>
          </div>
        </ng-container>

        <ng-container *ngIf="!isSummarizeActive && isTranslating">
          <div class="translate-loading-full">
            <div class="translate-spinner-ring"></div>
            <p class="translate-loading-text">Translating your document...</p>
          </div>
        </ng-container>

        <ng-container
          *ngIf="!isSummarizeActive && !isTranslating && translatedHtml"
        >
          <div class="doc-toolbar translate-result-toolbar">
            <div class="translate-result-lang-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6c2bd9"
                stroke-width="1.8"
              >
                <path
                  d="M3 5h12M9 3v2m-3.232 9.232a7.5 7.5 0 0010.607 0M21 21l-6-6m6 6l-6-6"
                />
              </svg>
              <app-dropdown
                [options]="supportedLanguages"
                optionLabel="label"
                optionValue="value"
                class="translate-result-lang-select"
                [(ngModel)]="selectedLanguage"
                (onChange)="onLanguageChange()"
                [disabled]="isLoadingLanguages"
              ></app-dropdown>
            </div>
            <div class="doc-toolbar-controls">
              <button
                class="ctrl-btn"
                (click)="pageNo > 1 ? pageChange(pageNo - 1) : null"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              </button>
              <input
                class="page-input"
                type="number"
                [min]="1"
                [max]="numPdfPages"
                [(ngModel)]="pageJumpValue"
                (keydown.enter)="jumpToPage()"
                (blur)="jumpToPage()"
              />
              <span class="page-sep-text">/ {{ numPdfPages || "—" }}</span>
              <button
                class="ctrl-btn"
                (click)="pageNo < numPdfPages ? pageChange(pageNo + 1) : null"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div class="ctrl-sep"></div>
              <button
                class="ctrl-btn translate-back-btn"
                (click)="resetTranslatePdf()"
                title="Back to original"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.2"
                >
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              </button>
            </div>
          </div>
          <div class="pdf-card">
            <div
              class="docx-html-preview translated-content"
              [style.zoom]="zoomLevel / 100"
              [innerHTML]="translatedHtml"
            ></div>
          </div>
        </ng-container>
      </div> </ng-container
    ><!-- /translate tab -->
  </div>
  <!-- /main-content -->
</div>
<!-- /page-wrapper -->

<!-- ═══════════════════════════════════════════════════════════
     SUMMARIZE PANEL  (shared ng-template)
════════════════════════════════════════════════════════════ -->
<ng-template #summarizePanel>
  <div class="summarize-panel">
    <div class="summarize-header">
      <div class="summarize-header-left">
        <div class="summarize-header-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
            <path
              d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z"
            />
          </svg>
        </div>
        <span class="summarize-header-title">Document Summary</span>
        <span class="summarize-ai-badge" *ngIf="summaryContent">AI</span>
      </div>
      <button
        class="summarize-close-btn"
        (click)="clearSummary()"
        title="Close summary"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
    <div class="summarize-loading" *ngIf="isSummarizing">
      <div class="summarize-pulse-dots">
        <span></span><span></span><span></span>
      </div>
      <p class="summarize-loading-text">Analyzing your document...</p>
      <p class="summarize-loading-sub">This may take a few moments</p>
    </div>
    <div class="summarize-error-state" *ngIf="!isSummarizing && summaryError">
      <div class="summarize-error-icon">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ef4444"
          stroke-width="1.6"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <p class="summarize-error-text">{{ summaryError }}</p>
      <button class="summarize-retry-btn" (click)="triggerSummarize()">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
        >
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
        Try Again
      </button>
    </div>
    <div class="summarize-content" *ngIf="!isSummarizing && summaryContent">
      <div class="summarize-body" [innerHTML]="summaryContent"></div>
    </div>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════════════════════
     CONSENT OVERLAY  (outside .main-content to keep pointer-events)
════════════════════════════════════════════════════════════ -->
<div class="consent-overlay" *ngIf="displayConsentModal">
  <div class="consent-modal">
    <div class="consent-icon">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    </div>
    <h3 class="consent-title">Electronic Consent</h3>

    <p class="consent-text">{{ 'CONSENT.TEXT' | translate }}</p>

    <!-- Checkbox row + language dropdown on same line -->
    <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
      <label class="consent-check-row" style="margin-bottom: 0; flex: 1;">
        <span class="consent-checkbox-wrap">
          <input
            type="checkbox"
            class="consent-cb"
            (change)="onCheckboxChange($event)"
            [disabled]="signerData.length < 1"
          />
          <span class="consent-checkmark">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" stroke-width="2.5">
              <path d="M2 6l3 3 5-5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </span>
        <span class="consent-check-text">{{ 'CONSENT.CHECKBOX_LABEL' | translate }}</span>
      </label>
      <app-dropdown
        class="consent-language-dropdown"
        style="font-size: 0.75rem; flex-shrink: 0;"
        [options]="consentLanguages"
        optionLabel="label"
        optionValue="code"
        [sort]="false"
        [(ngModel)]="selectedConsentLang"
        appendTo="body"
        panelStyleClass="esign-consent-dropdown-panel"
        (onChange)="translate.use(selectedConsentLang)"
      ></app-dropdown>
    </div>

    <div style="font-size: 0.72rem; color: #9ca3af; margin-top: 6px;">
      ({{ 'CONSENT.HELPER_TEXT' | translate }})
    </div>

    <div class="consent-footer">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.8">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
      <span>Your data is protected with 256-bit encryption</span>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════
     WEBCAM MODAL  (NgBootstrap template)
════════════════════════════════════════════════════════════ -->
<ng-template #userImage let-modal>
  <div class="cam-modal">
    <div class="cam-view">
      <app-kyc-details
        [showWebcam]="showWebcam"
        (pictureTaken)="handleImage($event)"
      >
      </app-kyc-details>
    </div>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════════════════════
     OTP MODAL  (NgBootstrap template)
════════════════════════════════════════════════════════════ -->
<ng-template #otpScreen let-modal>
  <div class="modal-content">
    <div class="modal-body">
      <div class="text-center" style="zoom: 80%">
        <div *ngIf="!sendOtpLink" class="form-signin">
          <div class="radius_border text-left">
            <img
              alt=""
              class="mb-4"
              style="height: 3rem"
              src="../../assets/img/new-logo.png"
            />
            <div class="container">
              <div>
                <ng-container *ngIf="signatoryDetails?.is_sms; else emailOnlyA">
                  An OTP will be sent to {{ signatoryDetails?.email }} and
                  {{ signatoryDetails?.contact_number }} for verification.
                </ng-container>
                <ng-template #emailOnlyA>
                  An OTP will be sent to {{ signatoryDetails?.email }} for
                  verification.
                </ng-template>
              </div>
              <div class="mt-4">
                <button
                  (click)="resendOtp()"
                  class="btn btn-primary ms-2"
                  style="background-color: rgb(74, 11, 119)"
                >
                  Send OTP
                </button>
              </div>
            </div>
          </div>
        </div>
        <div *ngIf="sendOtpLink" class="form-signin">
          <div class="radius_border text-left">
            <img
              alt=""
              class="mb-4"
              style="height: 3rem"
              src="../../assets/img/new-logo.png"
            />
            <div class="container">
              <div>
                <ng-container *ngIf="signatoryDetails?.is_sms; else emailOnlyB">
                  A 6-digit code has been sent to
                  {{ signatoryDetails?.email }} and
                  {{ signatoryDetails?.contact_number }}. Enter the code to
                  Proceed.
                </ng-container>
                <ng-template #emailOnlyB>
                  A 6-digit code has been sent to {{ signatoryDetails?.email }}.
                  Enter the code to Proceed.
                </ng-template>
              </div>
              <div class="tab-content mt-4">
                <div class="otp_section">
                  <ng-otp-input
                    #ngOtpInput
                    (onInputChange)="onOtpChange($event)"
                    *ngIf="showOtpComponent"
                    [config]="config"
                  >
                  </ng-otp-input>
                </div>
                <div class="row">
                  <div class="mt-3">
                    <button
                      [disabled]="enableOtp"
                      (click)="otpOnSubmit()"
                      class="btn btn-primary"
                      style="background-color: rgb(74, 11, 119)"
                    >
                      Submit
                    </button>
                    <button
                      *ngIf="timeLeft === 0"
                      (click)="resendOtp()"
                      class="btn btn-primary ms-2"
                      style="background-color: rgb(74, 11, 119)"
                    >
                      Resend OTP
                    </button>
                    <div class="font-weight-bold mt-3">
                      <span class="resend-otp">Resend OTP </span>
                      <span class="resend-otp-timer"
                        >in {{ timeLeft }} Sec</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════════════════════
     GEO-FENCING MODAL  (NgBootstrap template)
════════════════════════════════════════════════════════════ -->
<ng-template #geoFencingModal let-modal>
  <div class="modal-content geo-fencing-specific">
    <div class="modal-body text-center p-4">
      <div class="mb-4">
        <i class="fas fa-globe-americas fa-3x text-danger"></i>
      </div>
      <h5 class="mb-3 font-weight-bold">Access Restricted</h5>
      <p class="text-muted mb-3">
        You're protected by Doqfy's security. Document signing is not available
        from your current location.
      </p>
      <div class="alert alert-light border small mb-4">
        <div class="d-flex justify-content-between">
          <span class="text-muted">Detected Location:</span>
          <span class="font-weight-bold">{{ geoFencingData?.country }}</span>
        </div>
        <div class="d-flex justify-content-between mt-2">
          <span class="text-muted">IP Address:</span>
          <span class="font-weight-bold">{{ geoFencingData?.ip_address }}</span>
        </div>
      </div>
      <button
        type="button"
        class="btn btn-primary"
        (click)="modal.close('close')"
      >
        Acknowledge
      </button>
    </div>
  </div>
</ng-template>
